import { flattenPolygon } from 'utils/maps/geometry.utils';
import { safeJSONParse } from 'utils/data/parse.utils';

const STATE_NAMES_BY_FIPS: Record<string, string> = {
  '01': 'Alabama',
  '02': 'Alaska',
  '04': 'Arizona',
  '05': 'Arkansas',
  '06': 'California',
  '08': 'Colorado',
  '09': 'Connecticut',
  '10': 'Delaware',
  '11': 'District of Columbia',
  '12': 'Florida',
  '13': 'Georgia',
  '15': 'Hawaii',
  '16': 'Idaho',
  '17': 'Illinois',
  '18': 'Indiana',
  '19': 'Iowa',
  '20': 'Kansas',
  '21': 'Kentucky',
  '22': 'Louisiana',
  '23': 'Maine',
  '24': 'Maryland',
  '25': 'Massachusetts',
  '26': 'Michigan',
  '27': 'Minnesota',
  '28': 'Mississippi',
  '29': 'Missouri',
  '30': 'Montana',
  '31': 'Nebraska',
  '32': 'Nevada',
  '33': 'New Hampshire',
  '34': 'New Jersey',
  '35': 'New Mexico',
  '36': 'New York',
  '37': 'North Carolina',
  '38': 'North Dakota',
  '39': 'Ohio',
  '40': 'Oklahoma',
  '41': 'Oregon',
  '42': 'Pennsylvania',
  '44': 'Rhode Island',
  '45': 'South Carolina',
  '46': 'South Dakota',
  '47': 'Tennessee',
  '48': 'Texas',
  '49': 'Utah',
  '50': 'Vermont',
  '51': 'Virginia',
  '53': 'Washington',
  '54': 'West Virginia',
  '55': 'Wisconsin',
  '56': 'Wyoming',
  '60': 'American Samoa',
  '66': 'Guam',
  '69': 'Northern Mariana Islands',
  '72': 'Puerto Rico',
  '78': 'U.S. Virgin Islands',
};

// Helpers for parsing and normalizing jurisdiction coordinates.
// Keep these local to hooks so the rest of the app's geometry helpers remain untouched.

export const getJurisdictionState = (
  jurisdiction?: {
    GEOID?: string | number;
    state?: string;
    state_long?: string;
    state_name?: string;
  } | null,
): string => {
  const suppliedState = jurisdiction?.state ?? jurisdiction?.state_long ?? jurisdiction?.state_name;
  if (suppliedState) return suppliedState;

  const geoid = String(jurisdiction?.GEOID ?? '').padStart(5, '0');
  return STATE_NAMES_BY_FIPS[geoid.slice(0, 2)] ?? '';
};

/**
 * Convert GeoJSON-style coordinates ([lng, lat]) into [lat, lng].
 * Accepts a point, ring, or nested multipolygon and preserves structure.
 * @param input Coordinates input (point | ring | multipolygon)
 * @returns Converted coordinates with latitude first
 */
export function geoJsonToLatLng(input: any): any {
  if (!Array.isArray(input)) return input;
  if (input.length === 0) return [];
  // If this is a point [lng, lat]
  if (typeof input[0] === 'number') {
    const [lng, lat] = input as [number, number];
    return [lat, lng];
  }
  return input.map(geoJsonToLatLng);
}

/**
 * Parse a jurisdiction's `original_coordinates` JSON and return a flattened
 * polygon ring of `[lat, lng]` pairs.
 * @param jurisdiction Object containing `original_coordinates` string
 * @returns Array of [lat, lng] pairs (may be empty)
 */
export const buildJurisdictionPolygons = (jurisdiction: any): any[] => {
  try {
    const parsed = safeJSONParse((jurisdiction as any)?.original_coordinates, null);
    if (!parsed) return [];

    // Convert GeoJSON [lng, lat] -> [lat, lng] and flatten to a single ring
    const converted = geoJsonToLatLng(parsed);
    const flat = flattenPolygon(converted);

    return Array.isArray(flat)
      ? flat.filter(
          (pt) =>
            Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1]),
        )
      : [];
  } catch (error) {
    return [];
  }
};

/**
 * Normalize a list of jurisdiction objects into an array of polygon rings.
 * Accepts objects with `polygon` already parsed or `original_coordinates` JSON.
 * @param jurisdictions Array of jurisdiction-like objects
 * @returns Array of polygon rings ([[lat, lng], ...])
 */
export const getNormalizedJurisdictionPolygons = (
  jurisdictions: Array<{ polygon?: any; original_coordinates?: string }> = [],
): any[] => {
  if (!Array.isArray(jurisdictions) || jurisdictions.length === 0) return [];

  const result: any[] = [];
  for (const j of jurisdictions) {
    if (Array.isArray(j?.polygon) && j.polygon.length) {
      const poly = j.polygon;
      const isSingleRing = Array.isArray(poly[0]) && typeof (poly[0] as any)[0] === 'number';
      const rings = isSingleRing ? ([poly as any] as any[]) : (poly as any[]);

      for (const ring of rings) {
        if (!Array.isArray(ring)) continue;
        const cleaned = ring.filter(
          (pt) =>
            Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1]),
        );
        if (cleaned.length) result.push(cleaned);
      }
    } else {
      const parsed = buildJurisdictionPolygons(j as any as any);
      if (parsed && parsed.length) result.push(parsed);
    }
  }

  return result;
};
